import { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';

/**
 * Determina si un residente es moroso basado en el período de gracia
 * Debe coincidir EXACTAMENTE con la lógica del backend
 * 
 * @param {number} adeudo - Adeudo total actual
 * @param {number} cuotaMensual - Cuota mensual configurada
 * @param {number} mesesPermitidos - Meses de gracia permitidos (default: 1)
 * @returns {boolean} true si es moroso, false si no
 * 
 * @example
 * // Con cuota $570 y 2 meses de gracia:
 * calcularEstadoMoroso(1, 570, 2)      // false - $1 < $1,140
 * calcularEstadoMoroso(570, 570, 2)    // false - $570 < $1,140 (1 mes)
 * calcularEstadoMoroso(1140, 570, 2)   // true - $1,140 >= $1,140 (2 meses)
 * calcularEstadoMoroso(1710, 570, 2)   // true - $1,710 >= $1,140 (3 meses)
 */
export const calcularEstadoMoroso = (adeudo, cuotaMensual, mesesPermitidos = 1) => {
    const adeudoNum = Number(adeudo ?? 0);
    // Sin adeudo = no moroso
    if (!adeudoNum || adeudoNum <= 0) {
        return false;
    }

    // Meses de gracia configurados (soporta 0 explícito)
    const meses = Number(mesesPermitidos !== undefined && mesesPermitidos !== null ? mesesPermitidos : 1);

    // Si meses permitidos es 0, cualquier residente con adeudo >= 1 (o adeudo > 0) es moroso de inmediato
    if (meses === 0) {
        return Math.round(adeudoNum * 100) >= 100;
    }

    const cuota = Number(cuotaMensual ?? 0);
    // Si no hay cuota configurada, usar lógica anterior como fallback
    if (!cuota || cuota <= 0) {
        return adeudoNum > 0;
    }

    // Calcular umbral de morosidad: cuotaMensual × meses permitidos
    // El residente puede deber HASTA el umbral sin ser moroso.
    // Solo es moroso si el adeudo SUPERA el umbral permitido (ej. cuota=$850, meses=1 -> $850 no moroso, $851 sí moroso)
    const umbralMorosidad = cuota * meses;
    return Math.round(adeudoNum * 100) > Math.round(Number(umbralMorosidad) * 100);
};

/**
 * Hook personalizado para obtener configuración financiera en tiempo real
 * Útil para componentes que necesitan calcular estado moroso
 * 
 * @param {string} fraccionamientoId - ID del fraccionamiento
 * @returns {Object} Configuración financiera con cuotaMensual, mesesPermitidos y loading
 * 
 * @example
 * const { cuotaMensual, mesesPermitidos, loading } = useConfiguracionFinanciera(fraccionamientoId);
 * const esMoroso = calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);
 */
export const useConfiguracionFinanciera = (fraccionamientoId) => {
    const [config, setConfig] = useState({
        cuotaMensual: 0,
        mesesPermitidos: 1,
        loading: true
    });

    useEffect(() => {
        if (!fraccionamientoId) {
            setConfig({ cuotaMensual: 0, mesesPermitidos: 1, loading: false });
            return;
        }

        // Suscripción en tiempo real para actualizaciones automáticas
        // Usar sintaxis Firebase v9 modular
        const docRef = doc(db, 'fraccionamientos', fraccionamientoId);

        const unsubscribe = onSnapshot(
            docRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const configFinanciera = data.configuracionFinanciera || {};

                    setConfig({
                        cuotaMensual: Number(configFinanciera.montoMensual || configFinanciera.cuotaMensual || 0),
                        mesesPermitidos: Number(configFinanciera.interesesMesesPermitidosDeuda ?? 1),
                        loading: false
                    });
                } else {
                    setConfig({ cuotaMensual: 0, mesesPermitidos: 1, loading: false });
                }
            },
            (error) => {
                console.error('Error obteniendo configuración financiera:', error);
                setConfig({ cuotaMensual: 0, mesesPermitidos: 1, loading: false });
            }
        );

        return () => unsubscribe();
    }, [fraccionamientoId]);

    return config;
};

/**
 * Obtiene la configuración financiera de forma asíncrona (una sola vez)
 * Útil para funciones que no pueden usar hooks
 * 
 * @param {string} fraccionamientoId - ID del fraccionamiento
 * @returns {Promise<Object>} Configuración financiera
 */
export const obtenerConfiguracionFinanciera = async (fraccionamientoId) => {
    if (!fraccionamientoId) {
        return { cuotaMensual: 0, mesesPermitidos: 1 };
    }

    try {
        const docRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const data = docSnap.data();
            const configFinanciera = data.configuracionFinanciera || {};

            return {
                cuotaMensual: Number(configFinanciera.montoMensual || configFinanciera.cuotaMensual || 0),
                mesesPermitidos: Number(configFinanciera.interesesMesesPermitidosDeuda ?? 1)
            };
        }
    } catch (error) {
        console.error('Error obteniendo configuración financiera:', error);
    }

    return { cuotaMensual: 0, mesesPermitidos: 1 };
};

/**
 * Calcula el adeudo total acumulado de una casa sumando el campo adeudo + adeudoInteres
 * @param {Object} casa - Objeto casa de Firestore
 * @returns {number} Adeudo total con intereses
 */
export const calcularAdeudoConInteres = (casa = {}) => {
    if (!casa) return 0;
    
    // En Firestore, el campo 'adeudo' ya es el adeudo total consolidado (incluyendo capital, intereses y cuotas).
    // Si 'adeudo' existe y es numérico, lo usamos directamente para evitar duplicar los intereses acumulados.
    if (casa.adeudo !== undefined && casa.adeudo !== null && !isNaN(Number(casa.adeudo))) {
        return Number(casa.adeudo);
    }

    const capital = Number(casa.adeudoCapital ?? casa.deuda ?? casa.montoAdeudo ?? 0);
    const interes = Number(casa.adeudoInteres ?? casa.adeudoIntereses ?? casa.intereses ?? casa.recargos ?? 0);

    return capital + interes;
};

/**
 * Determina si una vivienda o usuario corresponde a Caseta / Seguridad
 * Validación blindada multi-criterio (flags, roles, correos, nombres y números)
 * 
 * @param {Object} casa - Objeto de casa o usuario de Firestore
 * @returns {boolean} true si es caseta o personal de seguridad, false si es residente regular
 */
export const esCasaCaseta = (casa = {}) => {
    if (!casa) return false;
    if (casa.esRolCaseta === true) return true;

    const rol = String(casa.rol || casa.rolAsignado || casa.role || '').toLowerCase().trim();
    const perfil = String(casa.perfil || casa.profile || '').toLowerCase().trim();
    const email = String(casa.correo || casa.email || '').toLowerCase().trim();
    const nombre = String(casa.nombre || casa.nombre_residente || casa.nombreCompleto || casa.propietario || casa.residente || '').toLowerCase().trim();
    const numero = String(casa.numero || casa.numeroCasa || '').toLowerCase().trim();

    const terminosSeguridad = [
        'caseta',
        'guardia',
        'guard',
        'security',
        'vigilancia',
        'seguridad',
        'control-acceso',
        'control_acceso',
        'acceso',
        'portero',
        'porteria',
        'recepcion'
    ];

    if (terminosSeguridad.some(t => rol.includes(t) || perfil.includes(t))) return true;
    if (email.startsWith('caseta') || email.includes('caseta') || email.includes('guardia') || email.includes('vigilancia')) return true;
    if (nombre.startsWith('caseta') || nombre.includes('caseta') || nombre.includes('vigilancia')) return true;
    if (numero === 'caseta' || numero.startsWith('caseta')) return true;

    return false;
};

/**
 * Determina si una casa o residente califica como moroso según la sección de Residentes:
 * - Excluye perfiles de caseta
 * - Si saldoFavor > 0, NO es moroso
 * - Evalúa calcularEstadoMoroso con adeudo, cuotaMensual y mesesPermitidos
 */
export const esCasaMorosa = (casa = {}, cuotaMensual = 0, mesesPermitidos = 1) => {
    if (!casa || esCasaCaseta(casa)) return false;
    const saldoFavor = Number(casa.saldoFavor ?? 0);
    if (saldoFavor > 0) return false;
    const adeudo = Number(casa.adeudo ?? casa.deuda ?? casa.montoAdeudo ?? 0);
    return calcularEstadoMoroso(adeudo, cuotaMensual, mesesPermitidos);
};

/**
 * Obtiene el nombre registrado principalmente en la tarjeta del residente
 * Revisa en orden de prioridad los campos habituales de la casa o usuario.
 */
export const obtenerNombreResidente = (casa = {}) => {
    if (!casa) return '';
    const campos = [
        casa.nombre_residente,
        casa.nombre,
        casa.nombreTitular,
        casa.titular,
        casa.propietario,
        casa.arrendador,
        casa.nombrePropietario,
        casa.nombreArrendatario,
        casa.nombreCompleto,
        casa.displayName,
        casa.nombreUsuario,
        casa.usuarioNombre,
    ];
    for (const val of campos) {
        if (typeof val === 'string' && val.trim().length > 0) {
            return val.trim();
        }
    }
    return '';
};
